import { forwardRef } from "react";

import { Box, IconButton } from "@mui/material";
import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers/DatePicker";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

import { useDatePicker } from "./DatePicker.hooks";
import {
  DatePickerProps,
  DatePickerReference,
  DatePickerTextFieldProps,
} from "./DatePicker.types";
import { CalendarIcon, XIcon } from "../Icon";
import TextField from "../TextField/TextField";

const DatePickerTextField = ({
  InputProps,
  onClick,
  onChange,
  onResetValue,
  valueState,
  showClear,
  ...innerProps
}: DatePickerTextFieldProps) => {
  return (
    <TextField
      {...innerProps}
      onChangeEvent={onChange}
      onClick={onClick}
      InputProps={{
        ...(InputProps ?? {}),
        startAdornment: (
          <IconButton
            size="small"
            disabled={innerProps.disabled}
            sx={{ marginX: rem(-6) }}
            onClick={
              onClick as unknown as React.MouseEventHandler<HTMLButtonElement>
            }
          >
            <CalendarIcon fontSize="small" sx={{ color: gray[700] }} />
          </IconButton>
        ),
        endAdornment:
          showClear && valueState && !innerProps.disabled ? (
            <IconButton
              size="small"
              sx={{ marginX: rem(-6) }}
              onClick={(event) => {
                event.stopPropagation();
                onResetValue?.();
              }}
            >
              <XIcon
                sx={{ color: gray[500], width: rem(18), height: rem(18) }}
              />
            </IconButton>
          ) : null,
        // readOnly: true,
      }}
    />
  );
};

const DatePicker = forwardRef<DatePickerReference, DatePickerProps>(
  (
    {
      defaultValue,
      value,
      placeholder,
      required,
      helperText,
      tooltipText,
      error,
      showClear = true,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const {
      rootRef,
      valueState,
      openCalendar,
      onChangeHandler,
      onCalendarCloseHandler,
      toggleCalendar,
      onReset,
    } = useDatePicker({
      ref,
      defaultValue,
      value,
      onChange,
    });

    return (
      <Box ref={rootRef}>
        <MUIDatePicker
          value={valueState}
          open={openCalendar}
          onChange={onChangeHandler}
          onClose={onCalendarCloseHandler}
          slots={{ textField: DatePickerTextField }}
          dayOfWeekFormatter={(day) => day.slice(0, 2).toUpperCase()}
          slotProps={{
            popper: {
              sx: {
                ".MuiPickersPopper-paper": {
                  boxShadow: shadows.lg,
                },
              },
            },
            textField: {
              required,
              placeholder,
              helperText,
              error,
              showClear:
                showClear && !innerProps.disabled && !innerProps.readOnly,
              valueState,
              onResetValue: () => {
                onReset();
              },
              onClick: toggleCalendar as unknown as never,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
          }}
          {...innerProps}
        />
      </Box>
    );
  }
);

export default DatePicker;
